import PropTypes from 'prop-types';
import queryString from 'core/libs/query-string';

import { withRouter } from 'core/libs/router';

import { buildSearchLink } from 'core/utils/url-helper';

import SearchInput from 'core/components/SearchInput';
import Tag from 'core/components/Tag';
import Tags from 'core/components/Tags';
import Page from 'core/components/Page';
import PageLoader from 'core/components/Loader/PageLoader';

import SearchData from './SearchData';

import styles from './index.styl';

const emptyList = [];

function buildTagLink(mediaUrlPrefix, query, tagKey, tagValue) {
  const newParams = { query };
  if (tagKey && tagValue) {
    newParams[tagKey] = tagValue;
  }

  return {
    pathname: `${mediaUrlPrefix || ''}/search`,
    search: queryString.stringify(newParams),
    state: { preventScrollToTop: true },
  };
}

/**
 * @author Alexey Rezakov <a.rezakov@rambler-co.ru>
 */
function SearchPage({
  isLoading,
  history,
  filters,
  location: {
    search,
  },
  ad1,
  ad2,
  ad1Mobile,
  ad2Mobile,
  card,
  interitemSpacing,
  pagination,
  foundTextBlock,
  tagTypeActive,
  tagType,
  tagSize,
  dataQa,
  rawContent,
  iconSize,
}, context) {
  const params = queryString.parse(search);
  const { query } = params;

  const { mediaUrlPrefix } = context;
  let isActiveAll = true;

  const tagButtons = (filters || emptyList).map((item, index) => {
    const link = buildTagLink(mediaUrlPrefix, query, item.key, item.value);
    const isActiveFilter = params[item.key] === item.value;

    if (isActiveFilter) {
      isActiveAll = false;
    }

    return (
      <Tag
        key={index}
        type={isActiveFilter ? tagTypeActive : tagType}
        state={isActiveFilter ? 'idle' : 'auto'}
        inactive={isActiveFilter}
        link={link}
        minWidth={0}
        size={tagSize}
      >
        {item.title}
      </Tag>
    );
  });

  tagButtons.length > 0 && tagButtons.unshift(
    <Tag
      key={-1}
      type={isActiveAll ? tagTypeActive : tagType}
      inactive={isActiveAll}
      state={isActiveAll ? 'idle' : 'auto'}
      link={buildTagLink(mediaUrlPrefix, query)}
      minWidth={0}
      size={tagSize}
    >
      Все
    </Tag>
  );

  const contentElement = isLoading
    ? <PageLoader />
    : (
      <SearchData
        rawContent={rawContent}
        card={card}
        interitemSpacing={interitemSpacing}
        ad1={ad1}
        ad2={ad2}
        ad1Mobile={ad1Mobile}
        ad2Mobile={ad2Mobile}
        pagination={pagination}
        foundTextBlock={foundTextBlock}
        isLoading={isLoading}
      />);

  return (
    <Page title='Поиск'>
      <form
        className={styles.form}
        onSubmit={onSubmit.bind(this, history, mediaUrlPrefix)}
        onReset={onReset.bind(this, history, mediaUrlPrefix)}
        {...dataQa && { 'data-qa': dataQa + '-form' }}
      >
        <SearchInput
          name='query'
          size='medium'
          defaultValue={query}
          iconSize={iconSize}
          autoFocus
        />
        {tagButtons.length > 0 && (
          <div className={styles.tags}>
            <Tags dataQa={dataQa + '-tags'}>{tagButtons}</Tags>
          </div>
        )}
      </form>
      {contentElement}
    </Page>
  );
}

SearchPage.contextTypes = {
  mediaUrlPrefix: PropTypes.string,
};

SearchPage.defaultProps = {
  interitemSpacing: 30,
  tagTypeActive: 'secondary',
  tagType: 'ghost',
  tagSize: 'small',
};

SearchPage.propTypes = {
  /**
   * Показывать лоадер вместо SearchData.
   * Нужно для того, чтобы не перерендеривать форму с инпутом и фильтрами.
   */
  isLoading: PropTypes.bool,

  /**
   * Сырые данные для отображения на странице поиска
   */
  rawContent: PropTypes.object,

  /**
   * Компонент кастомного текста про найденные материалы
   */
  foundTextBlock: PropTypes.func,

  /**
   * Компонент кастомной паджинации
   */
  pagination: PropTypes.func,

  /**
   * @ignore
   */
  history: PropTypes.object,

  /**
   * @ignore
   */
  location: PropTypes.object,

  /**
   * Фильтры результатов поиска.
   */
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    })
  ),

  /**
   * Карточка для поиска.
   *
   * В `relationships.search_result` можно получить поля `searchTitle` и `searchBody`.<br/>
   * Таким образом найденная фраза может быть выделена тегом `<b>`.
   *
   * Посмотреть пример карточки можно здесь DemoSearchCard
   */
  card: PropTypes.func.isRequired,

  /**
   * Реклама после 5 элемента
   */
  ad1: PropTypes.func,

  /**
   * Реклама после 10 элемента
   */
  ad2: PropTypes.func,

  /**
   * Мобильная реклама после 5 элемента
   */
  ad1Mobile: PropTypes.func,

  /**
   * Мобильная реклама после 10 элемента
   */
  ad2Mobile: PropTypes.func,

  /**
   * Отступ между карточками
   */
  interitemSpacing: PropTypes.number,

  /**
   * Размер иконки в инпуте.
   * Иконка должна быть квадратом.
   * Свойство переопределяет width и height.
   */
  iconSize: PropTypes.number,

  tagSize: PropTypes.oneOf([
    'extraSmall',
    'small',
    'medium',
    'large',
    'extraLarge',
  ]),
  tagType: PropTypes.string,
  tagTypeActive: PropTypes.string,
  /** @ignore */
  dataQa: PropTypes.string,
};

function onSubmit(history, prefix, event) {
  event.preventDefault();

  if (event.target.query.value === '') {
    onReset(history, prefix, event);
    return;
  }
  history.push(buildSearchLink(prefix, event.target.query.value));
}

function onReset(history, prefix, event) {
  event.preventDefault();
  history.push(buildSearchLink(prefix));
}

const SearchPageWithHOCs = withRouter(SearchPage);
SearchPageWithHOCs.displayName = 'SearchPage';

export { SearchPage as StorybookComponent };
export default SearchPageWithHOCs;
